import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {MenuEntryList} from "@/shared/ui/contextMenu";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {AbstractWorkingTimeRequestResponseModel} from "@/shared/timr";
import {SecurityStatus} from "@/shared/securityStatus";

export interface WorkingTimeRequestTableSmallConfig extends BasicDataTableConfig {
	url: string;
	contextMenuEntries: MenuEntryList<AbstractWorkingTimeRequestResponseModel>;
	showUserInfo: boolean;
	showEditButton: boolean;
	defaultOrder: "asc" | "desc";
}

export class WorkingTimeRequestTableSmall extends BasicDataTable<AbstractWorkingTimeRequestResponseModel> {

	public static EVENT_SELECT = "select";
	public static EVENT_UNSELECT = "unselect";
	public static EVENT_EDIT = "edit";
	public static EVENT_SHOW = "show";

	private workingTimeRequestsCache: { [id: string]: AbstractWorkingTimeRequestResponseModel } = {};

	constructor(public config: WorkingTimeRequestTableSmallConfig) {
		super(config.url, config);

		this.dataTable.on("user-select", (event, _dt: DataTables.Api, type: string, _cell: DataTables.CellMethods, mouseEvent: JQuery.TriggeredEvent) => {
			// Cancel before DataTables changes selection so a double-click toggles the row only once.
			if (type === "row" && mouseEvent.detail > 1) {
				event.preventDefault();
			}
		});

		this.dataTable.on("select", (e, dt: DataTables.Api, type, indexes) => {
			const workingTimeRequests = dt.rows(indexes).data().toArray() as AbstractWorkingTimeRequestResponseModel[];
			if (!dt) return;
			if (workingTimeRequests.length === 1) {
				$(this).triggerHandler(WorkingTimeRequestTableSmall.EVENT_SELECT, [workingTimeRequests[0]]);
			}
		});
		this.dataTable.on("deselect", (e, dt: DataTables.Api, type, indexes) => {
			const workingTimeRequests = dt.rows(indexes).data().toArray() as AbstractWorkingTimeRequestResponseModel[];
			if (!dt) return;
			if (workingTimeRequests.length === 1) {
				$(this).triggerHandler(WorkingTimeRequestTableSmall.EVENT_UNSELECT, [workingTimeRequests[0]]);
			}
		});

		$(this).on(WorkingTimeRequestTableSmall.EVENT_DATA_CHANGED, () => {
			this.workingTimeRequestsCache = {};
		});

		this.config.element
			.on("click", ".btn-table-edit", (e) => {
				const workingTimeRequest = this.workingTimeRequestsCache[$(e.currentTarget).data("id") as string];
				$(this).triggerHandler(WorkingTimeRequestTableSmall.EVENT_EDIT, [workingTimeRequest]);
			})
			.on("click", ".btn-table-show", (e) => {
				const workingTimeRequest = this.workingTimeRequestsCache[$(e.currentTarget).data("id") as string];
				$(this).triggerHandler(WorkingTimeRequestTableSmall.EVENT_SHOW, [workingTimeRequest]);
			});
	}

	public deselectTable() {
		this.dataTable.rows().deselect();
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			select: {
				style: "single",
				selector: this.config.contextMenuEntries.length > 0 ? "td:not(:first-child)" : "td",
				info: false
			}
		};
	}

	protected getColumnSettings(): TypedColumnSettings<AbstractWorkingTimeRequestResponseModel>[] {
		return [
			{
				data: "id",
				visible: this.config.contextMenuEntries.length > 0,
				orderable: false,
				className: "context-menu",
				render: this.contextMenu.render,
				width: BasicDataTable.COLUMN_SIZE_MENU
			},
			{
				data: "start",
				orderable: false,
				render: (data, type, workingTimeRequest, meta) => this.renderContent(data, type, workingTimeRequest, meta)
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, this.config.defaultOrder]];
	}

	private renderContent(data: string, type: string, workingTimeRequest: AbstractWorkingTimeRequestResponseModel, meta: DataTables.CellMetaSettings) {
		let status: JQuery;
		if (this.config.showEditButton) {
			status = $("<button>").attr("data-id", workingTimeRequest.id);
			this.workingTimeRequestsCache[workingTimeRequest.id] = workingTimeRequest;
			if (WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) || WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest)) {
				status.addClass("btn-table-edit").attr("title", messages["requests.workingTimeRequestsApproval.popover.edit"]);
			} else {
				status.addClass("btn-table-show").attr("title", messages["requests.workingTimeRequestsApproval.popover.show"]);
			}
		} else {
			status = $("<span>");
		}
		status.addClass(`pull-right label request-status-${workingTimeRequest.status} request-category-${workingTimeRequest.workingTimeType.category}`)
			.append($("<span>").text(messages["workingTimeRequestStatus." + workingTimeRequest.status]));
		const proofStatus = $("<span>");
		if (workingTimeRequest.proofStatus && SecurityStatus.isProofsAllowed()) {
			proofStatus.addClass(`pull-right label ${this.config.showUserInfo ? "" : "ignore"} proof-status status-${workingTimeRequest.proofStatus}`)
				.append($("<span>").text(messages["proofStatus." + workingTimeRequest.proofStatus]));
		}
		const noticeBadge = $("<span>");
		if (WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest)) {
			noticeBadge.addClass("pull-right badge badge-notice").text(messages["requests.workingTimeRequestsUser.notice"]);
		}
		const dateRange = WorkingTimeRequestUtil.renderDateRange(workingTimeRequest);
		const content = $("<div>");
		if (this.config.showUserInfo) {
			content.append($("<p>").addClass("text-bold").text(workingTimeRequest.user.fullname).append(status))
				.append($("<p>").text(workingTimeRequest.workingTimeType.name).append(WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest) ? noticeBadge : proofStatus));
			content.append($("<p>").html(dateRange));
		} else {
			content.append($("<p>").addClass("text-bold").text(workingTimeRequest.workingTimeType.name).append(status));
			content.append($("<div>")
				.addClass("display-flex justify-content-space-between")
				.append($("<div>").html(dateRange))
				.append($("<div>").append(WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest) ? noticeBadge : proofStatus)));
		}
		if (workingTimeRequest.statusComment) {
			content.append($("<p>").text(workingTimeRequest.statusComment));
		}
		return content.html();
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntries(this.config.contextMenuEntries);
	}
}
